/**
 * Seedable deterministic RNG (mulberry32). Single injectable source: the
 * simulation must be reproducible from a seed (procgen, debugging, future
 * replay/netcode). Never call Math.random() in game logic.
 */
/**
 * FNV-1a 32-bit string hash — stable, platform-independent seed derivation
 * (e.g. the daily broadcast seeds from a UTC date string).
 */
export function hashString(s: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return h >>> 0
}

export class RNG {
  private s: number

  constructor(seed: number) {
    this.s = seed >>> 0
    if (this.s === 0) this.s = 0x9e3779b9
  }

  /** [0, 1) */
  next(): number {
    this.s = (this.s + 0x6d2b79f5) >>> 0
    let t = this.s
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }

  range(lo: number, hi: number): number {
    return lo + this.next() * (hi - lo)
  }

  /** integer in [lo, hi] inclusive */
  int(lo: number, hi: number): number {
    return lo + Math.floor(this.next() * (hi - lo + 1))
  }

  chance(p: number): boolean {
    return this.next() < p
  }

  pick<T>(arr: readonly T[]): T {
    if (arr.length === 0) throw new Error('RNG.pick on empty array')
    return arr[Math.floor(this.next() * arr.length)]!
  }

  shuffle<T>(arr: T[]): T[] {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = this.int(0, i)
      const tmp = arr[i]!
      arr[i] = arr[j]!
      arr[j] = tmp
    }
    return arr
  }

  /** Derive an independent stream (e.g. per-level) without disturbing this one. */
  fork(salt: number): RNG {
    return new RNG((this.s ^ Math.imul(salt + 1, 0x85ebca6b)) >>> 0)
  }
}
